import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { observer } from 'mobx-react-lite';
import { Suspense, type ComponentProps, type ReactNode } from 'react';

import { HolidayEffect } from '../../../assets/Holidays/HolidayEffect';
import { type LogoDestination } from '../../../assets/Logo/Logo';
import { theme } from '../../../theme/theme';
import { HeaderLogos } from './HeaderLogos';

export const HEADER_BACKGROUND_COLOR = theme.colors.primary;

export const BaseHeader = css`
	background-color: ${HEADER_BACKGROUND_COLOR};
	color: ${theme.font.colors.primaryDarkBackgroundContrast};
`;

const StyledHeader = styled.div`
	flex-wrap: nowrap;
	flex-shrink: 0;
	position: relative;
	${BaseHeader};
	padding-block: ${theme.paddings.base};
	padding-inline: ${theme.paddings.lg};

	min-block-size: 80px;

	column-gap: ${theme.paddings.xxs};

	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`;

const HeaderButtonsContainer = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	column-gap: ${theme.paddings.xs};
`;

const PageTitle = styled.span`
	font-weight: ${theme.font.weights.bold};
	font-size: ${theme.font.sizes.title4};
	color: ${theme.colors.white};
`;

const HeaderLogoAndTitleContainer = styled(HeaderButtonsContainer)`
	column-gap: ${theme.paddings.lg};
`;

const MainHeaderContainer = styled.div`
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
`;

const EmptyDiv = styled.div``;

export enum ApplicationsMenuSelectedPage {
	Quality = 'Quality',
	PatientPortrait = 'PatientPortrait',
	Dashboard = 'Dashboard',
	Admin = 'Admin',
	Files = 'Files',
}

export interface LayoutHeaderProps extends ComponentProps<typeof StyledHeader> {
	readonly logo?: ReactNode;
	readonly headerLogoLink?: LogoDestination;
	readonly headerAppMenuSelectedPage?: ApplicationsMenuSelectedPage;
	readonly mainHeaderContent?: ReactNode;
	readonly sideHeaderContent?: ReactNode;
	readonly applicationsMenu?: ReactNode;
	readonly userPreferences?: ReactNode;
	readonly headerTitle?: ReactNode;
}

const AntDesignHeaderConfigProvider = function AntDesignHeaderConfigProvider(
	props: ComponentProps<typeof ConfigProvider>,
) {
	const { primaryDarkBackgroundContrast, secondaryDarkBackgroundContrast, tertiaryDarkBackgroundContrast } =
		theme.font.colors;

	return (
		<ConfigProvider
			{...{
				theme: {
					algorithm: antdTheme.darkAlgorithm,
					components: {
						Button: {
							primaryShadow: '0',
							colorText: primaryDarkBackgroundContrast,
							colorTextDisabled: tertiaryDarkBackgroundContrast,
							colorLink: primaryDarkBackgroundContrast,
							colorLinkHover: secondaryDarkBackgroundContrast,
						},
						Form: {
							colorTextHeading: theme.colors.white,
						},
						Typography: {
							titleMarginBottom: '0',
							titleMarginTop: '0',
							colorTextHeading: theme.colors.white,
						},
					},
				},
				...props,
			}}
		/>
	);
};

export const MainLayoutHeader = observer(function MainLayoutHeader({
	headerLogoLink,
	headerAppMenuSelectedPage: _headerAppMenuSelectedPage,
	logo = <HeaderLogos {...{ headerLogoLink }} />,
	mainHeaderContent,
	sideHeaderContent,
	applicationsMenu = null,
	userPreferences = null,
	headerTitle,
	...props
}: LayoutHeaderProps) {
	return (
		<StyledHeader {...props}>
			<AntDesignHeaderConfigProvider>
				<HeaderLogoAndTitleContainer>
					{logo}
					{Boolean(headerTitle) && <PageTitle>{headerTitle}</PageTitle>}
					<HolidayEffect />
				</HeaderLogoAndTitleContainer>
				<MainHeaderContainer>
					<Suspense {...{ fallback: <EmptyDiv /> }}>{mainHeaderContent}</Suspense>
				</MainHeaderContainer>
				<HeaderButtonsContainer>
					<Suspense {...{ fallback: <EmptyDiv /> }}>{sideHeaderContent}</Suspense>
					{userPreferences}
					{applicationsMenu}
				</HeaderButtonsContainer>
			</AntDesignHeaderConfigProvider>
		</StyledHeader>
	);
});
